/**
 * BFF error → localized human message. The wire contract carries stable
 * `FlowIssue.code`s (plus English `message` prose); we translate by code and
 * only fall back to the server prose for codes this map doesn't know.
 *
 * `email_challenge_failed` is one code with five causes — the BFF
 * distinguishes them only in the message text, so those exact strings are
 * matched here (they live in this monorepo; the pairing is intentional).
 */

import { PortalApiError } from '../api/portal-client'
import type { Messages } from './messages'

const EMAIL_CHALLENGE_BY_MESSAGE: Record<string, (m: Messages['errors']) => string> = {
  'Invalid or expired code': (m) => m.emailChallengeGeneric,
  'This code was already used': (m) => m.emailChallengeUsed,
  'This code has expired': (m) => m.emailChallengeExpired,
  'Too many attempts': (m) => m.emailChallengeTooMany,
  'Incorrect code': (m) => m.emailChallengeIncorrect
}

const BY_CODE: Record<string, (m: Messages['errors']) => string> = {
  validation_error: (m) => m.validationError,
  unauthorized: (m) => m.unauthorized,
  forbidden: (m) => m.forbidden,
  csrf_failed: (m) => m.csrfFailed,
  not_found: (m) => m.notFound,
  payload_too_large: (m) => m.payloadTooLarge,
  conflict: (m) => m.conflict,
  rate_limited: (m) => m.rateLimited,
  webauthn_failed: (m) => m.webauthnFailed,
  email_challenge_failed: (m) => m.emailChallengeGeneric,
  not_provisioned: (m) => m.notProvisioned,
  access_forbidden: (m) => m.accessForbidden,
  access_step_up_required: (m) => m.accessStepUpRequired,
  member_not_found: (m) => m.memberNotFound,
  last_admin: (m) => m.lastAdmin,
  access_self_change: (m) => m.accessSelfChange,
  invite_domain_not_allowed: (m) => m.inviteDomainNotAllowed,
  passkey_reset_not_allowed: (m) => m.passkeyResetNotAllowed,
  tenant_not_found: (m) => m.tenantNotFound,
  tenant_exists: (m) => m.tenantExists,
  upstream_failed: (m) => m.upstreamFailed,
  upstream_misconfigured: (m) => m.upstreamMisconfigured,
  execution_in_flight: (m) => m.executionInFlight,
  memory_not_found: (m) => m.memoryNotFound,
  memory_not_yours: (m) => m.memoryNotYours,
  memory_conflict: (m) => m.memoryConflict,
  memory_full: (m) => m.memoryFull,
  memory_unavailable: (m) => m.memoryUnavailable,
  memory_not_configured: (m) => m.memoryNotConfigured,
  memory_off: (m) => m.memoryOff,
  memory_too_long: (m) => m.memoryTooLong,
  memory_title_required: (m) => m.memoryTitleRequired,
  requester_unresolved: (m) => m.requesterUnresolved,
  unexpected: (m) => m.unexpected,
  feedback_link_invalid: (m) => m.feedbackLinkInvalid,
  feedback_unavailable: (m) => m.feedbackUnavailable,
  flow_not_available: (m) => m.flowNotAvailable,
  flow_not_offered: (m) => m.flowNotOffered,
  flow_access_conflict: (m) => m.flowAccessConflict,
  // Files — the BFF's own codes and fr-docs' (a launch or reply fr-docs refused reaches the SPA as it answered).
  attachment_too_large: (m) => m.attachmentTooLarge,
  attachment_fetch_failed: (m) => m.attachmentFetchFailed,
  attachment_integrity_failed: (m) => m.attachmentIntegrityFailed,
  attachment_source_forbidden: (m) => m.attachmentSourceForbidden,
  sandbox_staging_failed: (m) => m.sandboxStagingFailed,
  deliverable_unavailable: (m) => m.deliverableUnavailable,
  upload_not_found: (m) => m.uploadNotFound,
  upload_expired: (m) => m.uploadExpired,
  upload_not_ready: (m) => m.uploadNotReady,
  upload_part_invalid: (m) => m.uploadPartInvalid,
  upload_incomplete: (m) => m.uploadIncomplete,
  upload_busy: (m) => m.uploadBusy,
  result_pending: (m) => m.resultPending,
  result_not_found: (m) => m.resultNotFound
}

/**
 * Refusals that concern the files a launch or a reply named, not the run itself: the person attaches a file again and
 * sends once more. The SPA then asks the server which finished uploads it still holds (`revalidate`), so no chip keeps
 * claiming "ready" for one it refused.
 */
export const UPLOAD_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'upload_expired',
  'upload_not_found',
  'upload_not_ready'
])

/** The catalog's words for a code, or null when the catalog does not know it. Never the server's prose. */
export function codeMessage(t: Messages, code: string | undefined): string | null {
  const byCode = code ? BY_CODE[code] : undefined
  return byCode ? byCode(t.errors) : null
}

/** Localize any error a portal API call can throw. Always returns something renderable. */
export function describeApiError(t: Messages, err: unknown): string {
  if (err instanceof PortalApiError) {
    if (err.status === 401) return t.common.sessionExpired
    const issue = err.issue
    if (issue) {
      if (issue.code === 'email_challenge_failed') {
        const refined = EMAIL_CHALLENGE_BY_MESSAGE[issue.message]
        if (refined) return refined(t.errors)
      }
      const byCode = BY_CODE[issue.code]
      if (byCode) return byCode(t.errors)
      // Unknown code — the server prose is better than a shrug.
      return issue.message || t.common.requestFailed(err.status)
    }
    return t.common.requestFailed(err.status)
  }
  if (err instanceof Error && err.message) return err.message
  return t.common.genericError
}
